import { useDispatch, useSelector } from "react-redux";
import IaListening from "../Home/index";
import ContentPage from "../ContentPage/index";
import { useEffect, useRef, useState } from "react";
import { setListening, setSpeaking, setIdle } from "../../redux/slices/aiSlice";
import { stockNews } from "../../components/data/newsData";
import {
  contentTypeEnum,
  TABLE_NAMES,
  GRAPH_NAMES,
  METHOD_NAMES,
  SIMULATION_MAP,
} from "../../util/enums";
import {
  addMessageToHistory,
  similarity,
  trimHistoryToLimit,
} from "../../util/variousMethods";
import { getEphemeralKey, toolRunSimulation } from "../../util/iaUtilMethods";
import LoadingOverlay from "../../components/LoadingOverlay";
import { HistoricalScenarios } from "../../services/riskFactor/riskData";
import { runHistoricalScenario } from "../../services/riskFactor/riskFunction";

async function connectWithEphemeralKey(offer) {
  const ephemeralKey = await getEphemeralKey();
  if (!ephemeralKey) throw new Error("❌ No se obtuvo ephemeral key");

  const url = "https://api.openai.com/v1/realtime/calls?model=gpt-realtime";

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${ephemeralKey}`,
      "Content-Type": "application/sdp",
    },
    body: offer.sdp,
  });

  if (!res.ok) {
    const errText = await res.text();
    console.error("❌ Error respuesta OpenAI:", errText);
    throw new Error(`OpenAI Realtime API error: ${errText}`);
  }

  const answerSdp = await res.text();
  return { sdp: answerSdp };
}

export default function IaDashboard() {
  const dispatch = useDispatch();
  const SIM_THRESHOLD = 0.6;
  const CHARACTER_LIMIT = 12000;
  const activeView = useSelector((s) => s.uiSlice.activeView);

  const pcRef = useRef(null);
  const dcRef = useRef(null);
  const micStreamRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const [outputText, setOutputText] = useState("Nothing");
  const [micLevel, setMicLevel] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const aiResponseRef = useRef("");
  const [conversationHistory, setConversationHistory] = useState([]);
  const [currentContentType, setCurrentContentType] = useState(
    contentTypeEnum.nothing
  );
  const isPortfolioLoadedRef = useRef(false);
  function safeSend(payload, maxRetries = 40, intervalMs = 100) {
    return new Promise((resolve, reject) => {
      let tries = 0;
      const attempt = () => {
        tries++;
        const ch = dcRef.current;
        if (!ch) {
          if (tries >= maxRetries) return reject(new Error("No data channel"));
          return setTimeout(attempt, intervalMs);
        }
        if (ch.readyState === "open") {
          try {
            ch.send(
              typeof payload === "string" ? payload : JSON.stringify(payload)
            );
            return resolve();
          } catch (err) {
            return reject(err);
          }
        } else {
          if (tries >= maxRetries)
            return reject(new Error("DataChannel not open"));
          setTimeout(attempt, intervalMs);
        }
      };
      attempt();
    });
  }

  const startMicrophoneAnalysis = (stream) => {
    try {
      if (
        typeof window === "undefined" ||
        (!window.AudioContext && !window.webkitAudioContext)
      ) {
        console.warn(
          "AudioContext not available, skipping microphone analysis"
        );
        return;
      }

      const audioContext = new (window.AudioContext ||
        window.webkitAudioContext)();

      if (audioContext.state === "suspended") {
        audioContext
          .resume()
          .then(() => {
            console.log("AudioContext resumed for microphone analysis");
          })
          .catch((err) => {
            console.error("Failed to resume AudioContext:", err);
            return;
          });
      }

      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);

      analyser.fftSize = 256;
      analyser.smoothingTimeConstant = 0.3;
      microphone.connect(analyser);

      audioContextRef.current = audioContext;
      analyserRef.current = analyser;

      analyzeMicrophoneLevel();
    } catch (error) {
      console.error("Error setting up audio analysis:", error);
      setMicLevel(0);
    }
  };

  const analyzeMicrophoneLevel = () => {
    if (!analyserRef.current) return;

    try {
      const analyser = analyserRef.current;
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      const checkLevel = () => {
        if (!analyserRef.current) return;

        try {
          analyser.getByteTimeDomainData(dataArray);
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            const sample = (dataArray[i] - 128) / 128;
            sum += sample * sample;
          }
          const rms = Math.sqrt(sum / bufferLength);

          const level = Math.min(100, Math.max(0, rms * 300));
          setMicLevel(level);

          if (analyserRef.current) {
            animationFrameRef.current = requestAnimationFrame(checkLevel);
          }
        } catch (error) {
          console.error("Error in microphone level analysis:", error);
          setMicLevel(0);
        }
      };

      checkLevel();
    } catch (error) {
      console.error("Error starting microphone level analysis:", error);
      setMicLevel(0);
    }
  };

  const stopMicrophoneAnalysis = () => {
    try {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      analyserRef.current = null;
      if (
        audioContextRef.current &&
        audioContextRef.current.state !== "closed"
      ) {
        audioContextRef.current.close().catch((err) => {
          console.warn("Error closing AudioContext:", err);
        });
        audioContextRef.current = null;
      }
      setMicLevel(0);
    } catch (error) {
      console.error("Error stopping microphone analysis:", error);
      setMicLevel(0);
    }
  };

  useEffect(() => {
    window.gptRealtimeControl = {
      stop: () => {
        if (micStreamRef.current) {
          micStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = false;
          });
        }
        dispatch(setIdle());
      },
      continue: () => {
        console.log("🎤 Unmuting microphone...");
        if (micStreamRef.current) {
          micStreamRef.current.getAudioTracks().forEach((track) => {
            track.enabled = true;
          });
        }
        dispatch(setListening());
      },
      getMicLevel: () => {
        if (
          currentContentType === contentTypeEnum.nothing &&
          analyserRef.current
        ) {
          return micLevel;
        }
        return 0;
      },
    };

    return () => {
      if (window.gptRealtimeControl) {
        delete window.gptRealtimeControl;
      }
    };
  }, [dispatch, micLevel, currentContentType]);

  const startWebRTC = async () => {
    try {
      const pc = new RTCPeerConnection({
        iceServers: [
          { urls: "stun:stun.l.google.com:19302" },
          { urls: "stun:stun1.l.google.com:19302" },
        ],
      });
      pcRef.current = pc;

      const micStream = await navigator.mediaDevices.getUserMedia({
        audio: true,
      });
      micStreamRef.current = micStream;
      micStream.getTracks().forEach((track) => pc.addTrack(track, micStream));

      setTimeout(() => {
        if (currentContentType === contentTypeEnum.nothing) {
          startMicrophoneAnalysis(micStream);
        }
      }, 1000);

      let audioCtx = null;
      let analyser = null;
      let dataArray = null;
      let volumeLoopRunning = false;

      pc.ontrack = (event) => {
        const audioEl = document.createElement("audio");
        audioEl.srcObject = event.streams[0];
        audioEl.autoplay = true;
        document.body.appendChild(audioEl);

        if (!audioCtx) {
          audioCtx = new AudioContext();
          analyser = audioCtx.createAnalyser();
          analyser.fftSize = 256;
          dataArray = new Uint8Array(analyser.frequencyBinCount);

          if (!volumeLoopRunning) {
            volumeLoopRunning = true;
            checkVolume();
          }
        }

        const source = audioCtx.createMediaStreamSource(event.streams[0]);
        source.connect(analyser);
      };

      let quietFrames = 0;
      const QUIET_LIMIT = 12;

      function checkVolume() {
        analyser.getByteTimeDomainData(dataArray);

        let sum = 0;
        for (let i = 0; i < dataArray.length; i++) {
          const val = dataArray[i] - 128;
          sum += val * val;
        }
        const rms = Math.sqrt(sum / dataArray.length);

        const threshold = 10;
        const speakingNow = rms > threshold;

        if (rms > 10) {
          quietFrames = 0;
          dispatch(setSpeaking());
        } else if (quietFrames < QUIET_LIMIT) {
          quietFrames += 1;
        } else {
          dispatch(setListening());
        }
        requestAnimationFrame(checkVolume);
      }

      const dataChannel = pc.createDataChannel("oai-events");
      dcRef.current = dataChannel;

      dataChannel.onopen = async () => {
        try {
          const savedHistory = localStorage.getItem("conversationHistory");
          if (savedHistory) {
            const parsed = JSON.parse(savedHistory);
            const trimmedHistory = trimHistoryToLimit(parsed, CHARACTER_LIMIT);
            setConversationHistory(trimmedHistory);
            for (const msg of trimmedHistory) {
              await safeSend({
                type: "conversation.item.create",
                item: {
                  role: "system",
                  type: "message",
                  content: [
                    {
                      type: "input_text",
                      text: `[Conversation history for context only, do not re-execute tools or functions] ${msg.role}: ${msg.content}`,
                    },
                  ],
                },
              });
            }
          }

          await safeSend({
            type: "response.create",
            conversation: "default",
            response: {
              conversation: "auto",
              instructions: "Start transcribing the user's audio",
            },
          });
        } catch (err) {
          console.error("❌ Error enviando en onopen:", err);
        }
      };

      dataChannel.onmessage = async (event) => {
        try {
          const data = JSON.parse(event.data);
          if (
            data.type ===
            "conversation.item.input_audio_transcription.completed"
          ) {
            const transcript = data?.transcript;
            if (
              !transcript ||
              typeof transcript !== "string" ||
              transcript.trim() === ""
            ) {
              return;
            }

            const clean = transcript.trim().toLowerCase();

            addMessageToHistory(
              setConversationHistory,
              "user",
              clean,
              CHARACTER_LIMIT
            );
          }
          if (data.type === "response.output_audio_transcript.delta") {
            dispatch(setSpeaking());
            aiResponseRef.current += data.delta;
          }
          if (data.type === "response.done") {
            const finalAiMsg = aiResponseRef.current.trim();
            if (finalAiMsg) {
              addMessageToHistory(
                setConversationHistory,
                "assistant",
                finalAiMsg,
                CHARACTER_LIMIT
              );
            }
            aiResponseRef.current = "";
          }
          if (data.type === "response.done" && data.response?.output) {
            for (const item of data.response.output) {
              if (item.type === "function_call") {
                const { name, arguments: args } = item;
                console.log("🔧 [APP-LEGACY]", window.location.href, "Tool called:", name, "args:", args);
                let result = null;
                let confirmationMessage = "";

                if (name != "showChart" && !isPortfolioLoadedRef.current) {
                  sendToolResponse(
                    "You must load the portfolio chart first.",
                    "You must load the portfolio chart first."
                  );
                  return;
                }

                // === mostrarGrafica ===
                if (name === "showChart") {
                  const { name: graphName } = JSON.parse(args || "{}");
                  let bestMatch = { key: null, score: 0 };
                  for (const key of Object.keys(GRAPH_NAMES)) {
                    const score = similarity(graphName, key);
                    if (score > bestMatch.score) bestMatch = { key, score };
                  }

                  if (bestMatch.score >= SIM_THRESHOLD) {
                    setCurrentContentType(contentTypeEnum.graph);
                    result = GRAPH_NAMES[bestMatch.key];
                    setOutputText(result);
                    isPortfolioLoadedRef.current = true;
                    confirmationMessage = `This is the portfolio "${bestMatch.key}" composition.`;
                    var resultToIa = {
                      title: bestMatch.key,
                      values: result?.data ?? [],
                    };
                    sendToolResponse(resultToIa, confirmationMessage);
                    return;
                  } else {
                    setCurrentContentType(contentTypeEnum.nothing);
                    setOutputText("Nothing");
                    result = { error: "Portfolio not found" };
                    confirmationMessage =
                      "I could not find the portfolio you requested.";
                  }
                }
                if (name === "showTable") {
                  const { name: tablaName } = JSON.parse(args || "{}");
                  let bestMatch = { key: null, score: 0 };

                  for (const key of Object.keys(TABLE_NAMES)) {
                    const score = similarity(tablaName, key);
                    if (score > bestMatch.score) bestMatch = { key, score };
                  }

                  if (bestMatch.score >= SIM_THRESHOLD) {
                    setCurrentContentType(contentTypeEnum.table);
                    result = TABLE_NAMES[bestMatch.key];
                    setOutputText(result);
                    confirmationMessage = `Here you have the table of "${bestMatch.key}".`;
                    if (bestMatch.key === "YTD Performance by index") {
                      confirmationMessage = `Here you have the table of YTD Performance by index. Here is the comparison of the portfolio with the market `;
                    }
                  } else {
                    result = { error: "Table not found" };
                    setCurrentContentType(contentTypeEnum.nothing);
                    setOutputText("Nothing");
                    confirmationMessage = "I didn't find the requested table.";
                  }
                }

                // === verMetodo ===
                if (name === "showMethod") {
                  const result = METHOD_NAMES["Longitudinal Monte Carlo"];
                  setOutputText(result);
                  confirmationMessage = `Here is the method of "${result.name}" and his descritpion is "${result.description}".`;
                  setCurrentContentType(contentTypeEnum.text);
                  sendToolResponse(result, confirmationMessage);
                  return;
                }
                // === verNoticias ===
                if (name === "showNews") {
                  const titles = stockNews.map((n) => n.title);
                  const confirmationMessage =
                    titles.length > 0
                      ? "Here are the news articles. How can I help you?"
                      : "No news articles found.";

                  setOutputText(titles);
                  setCurrentContentType(contentTypeEnum.newsList);
                  sendToolResponse(stockNews, confirmationMessage);
                  return;
                }

                if (name === "showNewsDetail") {
                  const { title: newsTitle } = JSON.parse(args || "{}");

                  let bestMatch = { key: null, score: 0 };
                  for (const item of stockNews) {
                    const score = similarity(newsTitle, item.title);
                    if (score > bestMatch.score)
                      bestMatch = { key: item.title, score };
                  }

                  if (bestMatch.score >= SIM_THRESHOLD) {
                    const newsItem = stockNews.find(
                      (n) => n.title === bestMatch.key
                    );

                    setCurrentContentType(contentTypeEnum.newsDetail);
                    setOutputText(newsItem);
                    confirmationMessage = `Here is the news titled: "${newsItem.title}".`;
                    result = newsItem;
                  } else {
                    setCurrentContentType(contentTypeEnum.nothing);
                    confirmationMessage =
                      "I did not find the news you requested.";
                    result = { error: "News article not found" };
                  }
                }

                if (name === "showListHistoricalRiskFactor") {
                  const scenarioTitles = Object.keys(HistoricalScenarios);
                  const confirmationMessage =
                    scenarioTitles.length > 0
                      ? "Here are the historical risk factor scenarios. How can I help you?"
                      : "No historical scenarios found.";
                  setOutputText(scenarioTitles);
                  setCurrentContentType(contentTypeEnum.riskHistoricalList);

                  setOutputText(scenarioTitles);
                  var resultToIa = {
                    title: "historical risk factor scenarios",
                    values: scenarioTitles,
                  };
                  sendToolResponse(
                    resultToIa,
                    confirmationMessage,
                    false,
                    true
                  );
                  return;
                }

                if (name === "showHistoricalRiskFactorDetail") {
                  const parsed = JSON.parse(args || "{}");
                  const scenarioTitle = parsed.title;

                  const scenarioTitles = Object.keys(HistoricalScenarios);
                  let bestMatch = { key: null, score: 0 };

                  for (const item of scenarioTitles) {
                    const score = similarity(scenarioTitle, item);
                    if (score > bestMatch.score) {
                      bestMatch = { key: item, score };
                    }
                  }

                  if (bestMatch.score >= SIM_THRESHOLD && bestMatch.key) {
                    const result = runHistoricalScenario(bestMatch.key);
                    setCurrentContentType(
                      contentTypeEnum.historicalRiskFactorDetail
                    );
                    setOutputText({
                      title: bestMatch.key,
                      data: result,
                    });

                    confirmationMessage = `Here is the historical risk factor scenario titled: "${bestMatch.key}".`;

                    var resultToIa = {
                      title: bestMatch.key,
                      values: result,
                    };
                    sendToolResponse(resultToIa, confirmationMessage);
                    return;
                  } else {
                    setCurrentContentType(contentTypeEnum.nothing);
                    confirmationMessage =
                      "I did not find the historical risk factor scenario you requested.";

                    result = { error: "Historical scenario not found" };
                    sendToolResponse(result, confirmationMessage);
                    return;
                  }
                }
                if (name === "showCustomRiskCalculation") {
                  window.gptRealtimeControl?.stop?.();
                  setCurrentContentType(contentTypeEnum.customRiskCalculation);
                  confirmationMessage = `Here is a form to calculate the risk factor.`;
                  sendToolResponse("", confirmationMessage, false, true);
                  return;
                }
                if (name === "showRiskFactorOptions") {
                  setCurrentContentType(contentTypeEnum.nothing);
                  confirmationMessage = `Do you want to view historical scenarios or calculate a custom scenario?`;
                  sendToolResponse("", confirmationMessage, false, true);
                  return;
                }

                if (name === "showTableByIndex") {
                  setCurrentContentType(contentTypeEnum.table);
                  result = TABLE_NAMES["YTD Performance by index"];
                  setOutputText(result);
                  confirmationMessage = `Here you have the table of YTD Performance by index. Here is the comparison of the portfolio with the market`;
                }

                if (SIMULATION_MAP[name]) {
                  sendToolResponse(
                    null,
                    "Running the simulation, this may take a little while...",
                    true
                  );
                  setIsLoading(true);
                  const { result, resultContent, confirmationMessage } =
                    await toolRunSimulation(SIMULATION_MAP[name]);

                  setCurrentContentType(
                    result && resultContent
                      ? contentTypeEnum.simulationTable
                      : contentTypeEnum.nothing
                  );
                  setOutputText(resultContent);
                  sendToolResponse(result, confirmationMessage);
                  setIsLoading(false);
                  return;
                }

                if (result) {
                  sendToolResponse(result, confirmationMessage);
                }
              }
            }
          }
        } catch (err) {
          console.error("❌ Error parseando datachannel:", err);
        }
      };

      const offer = await pc.createOffer({ offerToReceiveAudio: true });
      await pc.setLocalDescription(offer);

      const response = await connectWithEphemeralKey(offer);

      const remoteDesc = new RTCSessionDescription({
        type: "answer",
        sdp: response.sdp,
      });
      await pc.setRemoteDescription(remoteDesc);
      dispatch(setListening());
    } catch (err) {
      console.error("❌ Error en WebRTC:", err);
    }
  };
  function sendToolResponse(
    result,
    confirmationMessage,
    isPreliminary = false,
    isSpecialCase = false
  ) {
    if (!dcRef.current || dcRef.current.readyState !== "open") {
      console.error("DataChannel not ready");
      return;
    }

    if (!isPortfolioLoadedRef.current) {
      console.warn("❌ Portfolio no cargado. Cancelando flujo...");

      dcRef.current.send(
        JSON.stringify({
          type: "response.cancel",
          response: "auto",
        })
      );

      dcRef.current.send(
        JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [
              {
                type: "input_text",
                text: "You must load the portfolio first. How can I help you?",
              },
            ],
          },
        })
      );

      dcRef.current.send(
        JSON.stringify({
          type: "response.create",
          response: {
            conversation: "auto",
            instructions:
              "You must load the portfolio first. How can I help you?",
          },
        })
      );

      return;
    }

    if (!isPreliminary) {
      dcRef.current.send(
        JSON.stringify({
          type: "conversation.item.create",
          item: {
            type: "message",
            role: "system",
            content: [
              {
                type: "input_text",
                text: `Tool output (context only, do not describe or read this):\n${JSON.stringify(
                  result
                )}`,
              },
            ],
          },
        })
      );
    }

    let finalInstruction =
      `IMPORTANT: You just received JSON tool output. Do NOT describe or summarize the data. ` +
      `Only respond with: "${confirmationMessage} How can I help you?". ` +
      `You must NOT continue the conversation unless the user asks.`;

    if (isSpecialCase) {
      finalInstruction =
        `IMPORTANT: You just received tool output. ` +
        `Do NOT describe, read to the user or summarize anything. Only answer with this: "${confirmationMessage}".`;
    }

    if (isPreliminary) {
      finalInstruction = confirmationMessage;
    }

    dcRef.current.send(
      JSON.stringify({
        type: "response.create",
        response: {
          conversation: "auto",
          instructions: finalInstruction,
        },
      })
    );
  }

  const stopWebRTC = () => {
    if (pcRef.current) pcRef.current.close();
    stopMicrophoneAnalysis();
    setOutputText("Nothing");
  };

  useEffect(() => {
    startWebRTC();
    // stopWebRTC();
    return () => stopWebRTC();
  }, []);

  useEffect(() => {
    if (currentContentType !== contentTypeEnum.nothing) {
      stopMicrophoneAnalysis();
    } else if (
      micStreamRef.current &&
      currentContentType === contentTypeEnum.nothing
    ) {
      setTimeout(() => {
        startMicrophoneAnalysis(micStreamRef.current);
      }, 500);
    }
  }, [currentContentType]);

  const onBackFn = () => {
    window.gptRealtimeControl?.continue?.();
    if (currentContentType == contentTypeEnum.newsDetail) {
      const titles = stockNews.map((n) => n.title);

      dcRef.current.send(JSON.stringify({ type: "response.cancel" }));
      setOutputText(titles);
      setCurrentContentType(contentTypeEnum.newsList);
    } else {
      setCurrentContentType(contentTypeEnum.nothing);
      setOutputText("Nothing");
    }
  };

  return (
    <>
      <div
        style={{ height: "100%", minHeight: "100dvh", position: "relative" }}
      >
        {currentContentType != contentTypeEnum.nothing ? (
          <ContentPage
            chartData={outputText}
            contentType={currentContentType}
            onBack={onBackFn}
            sendToolResponse={sendToolResponse}
            setCurrentContentType={setCurrentContentType}
            setOutputText={setOutputText}
          />
        ) : (
          <IaListening />
        )}
      </div>
      <LoadingOverlay show={isLoading} message="Loading..." />
    </>
  );
}
